import React, { useState } from 'react';

import GlyphsCharacter from '@/components/global/GlyphsCharacter';
import Modal from '@/components/global/Modal';
import Panel from '@/components/global/Panel';
import PanelContent from '@/components/global/Panel/PanelContent';
import PanelHeader from '@/components/global/Panel/PanelHeader';
import GlyphModalContent from '@/components/global/PanelFont/PanelGlyphs/GlyphModalContent';
import { AWSClient } from '@/lib/aws/client';
import { useScreenSize } from '@/lib/hooks/mediaQuery';
import { entitify, updateFontsToWebfont } from '@/lib/util';

import FontLoader from '../../FontLoader';
import { getTranslatedCategoryLabel } from '../helpers';
import { FontStyleVersion } from '../types';
import styles from './PanelGlyphs.module.scss';

function createStyles(fontName) {
  return {
    __html: `
    :root {
      --${fontName}: '${fontName}';
    }
  `
  };
}

interface Props {
  fontSizeAdjust?: string;
  fontStyleVersionActive: FontStyleVersion;
  childrenHeader?: React.ReactNode;
  childrenContent?: React.ReactNode;
  childrenFooter?: React.ReactNode;
  fontStyleVersionChars?: {
    category: string;
    subCategory: string;
    script: string;
    chars: string[];
  }[];
  isLoading?: boolean;
}

const PanelGlyphs: React.FC<Props> = ({
  fontStyleVersionActive,
  childrenHeader,
  fontStyleVersionChars,
  fontSizeAdjust,
  childrenContent,
  childrenFooter
}) => {
  const screen = useScreenSize();
  const isMobile = screen === 'Mobile';

  const [glyphModalOpen, setGlyphModalOpen] = useState(false);
  const [modalButtonLabel, setModalButtonLabel] = useState(null);
  const [glyphData, setGlyphData] = useState(null);
  const [glyphDataCache, setGlyphDataCache] = useState({});

  const fontToLoad =
    fontStyleVersionActive &&
    updateFontsToWebfont({
      font: fontStyleVersionActive.fondue.meta.path
    });

  const fetchGlyphData = async (char, script, subCategory) => {
    const glyph = char;
    const glyphButtonLabel = (
      <>
        <div className={styles.glyphChar__Entity}>{`U+${char}`}</div>
        <div className={styles.glyphChar__Char}>{entitify(char)}</div>
      </>
    );

    const { path } = fontStyleVersionActive.fondue.meta;
    const { product, sku } = fontStyleVersionActive;
    const fontFamily = product.slug.toLowerCase();
    const fontStyle = sku.replace(/-pro$/, '');
    const [_pathWithoutExtension, pathExtension] = path.split('.');
    const version = fontStyleVersionActive.sku.match(/-pro$/) ? 'pro' : 'std';
    let glyphData = null;

    setGlyphData(null);
    setGlyphModalOpen(true);

    if (glyphDataCache[`${sku}-${char}`]) {
      glyphData = glyphDataCache[`${sku}-${char}`];
    } else {
      const glyphDataUrl = `${fontFamily}/${fontStyle}/glyphs/${glyph}`;
      const glyphDataRes = await AWSClient.fontGlyphsData('get', {
        params: { version, extension: pathExtension },
        pathAppend: glyphDataUrl
      });

      glyphData = glyphDataRes;

      setGlyphDataCache({ ...glyphDataCache, [`${sku}-${char}`]: glyphData });
    }

    setModalButtonLabel(glyphButtonLabel);
    setGlyphData({ ...glyphData, char, script, subCategory });
  };

  return (
    <div
      className={styles.PanelGlyphs}
      style={
        {
          ...(fontSizeAdjust && { '--glyph-size-adjust': fontSizeAdjust })
        } as React.CSSProperties
      }
    >
      <Modal
        splitModal
        controlled
        controlledOpen={glyphModalOpen}
        controlledSetOpen={setGlyphModalOpen}
        ModalButtonLabel={modalButtonLabel}
        {...(!isMobile && {
          closeButtonColor: 'var(--white)'
        })}
      >
        <GlyphModalContent glyphData={glyphData} />
      </Modal>
      {fontStyleVersionActive && (
        <div key={fontStyleVersionActive.fondue.meta.src}>
          <style dangerouslySetInnerHTML={createStyles(fontStyleVersionActive.fondue.slug)} />
          <FontLoader
            s3Path={fontToLoad}
            fontName={fontStyleVersionActive.fondue.slug}
            fullPath={!fontToLoad.includes('https://')}
          />
        </div>
      )}
      <Panel>
        <PanelHeader>{childrenHeader}</PanelHeader>
        <PanelContent>
          {childrenContent
            ? childrenContent
            : fontStyleVersionChars?.map((character, index) => {
                const { chars, script, category, subCategory } = character;
                const categoryLabel = getTranslatedCategoryLabel(category, subCategory);

                return (
                  <article
                    className={styles.PanelGlyphs__section}
                    key={`${character.category}-${subCategory}-${index}`}
                  >
                    <header className={styles.PanelGlyphs__sectionHeader}>
                      <h4 className={styles.PanelGlyphs__sectionHeaderTitle}>{categoryLabel}</h4>
                    </header>

                    {chars.map((char) => (
                      <GlyphsCharacter
                        key={char}
                        char={char}
                        entity={`U+${char}`}
                        script={script}
                        subCategory={subCategory}
                        glyphSheetName={fontStyleVersionActive.fondue.slug}
                        handler={fetchGlyphData}
                        as="button"
                      >
                        {entitify(char)}
                      </GlyphsCharacter>
                    ))}
                  </article>
                );
              })}
          {childrenFooter && <div>{childrenFooter}</div>}
        </PanelContent>
      </Panel>
    </div>
  );
};

export default PanelGlyphs;
